'use client'

import clsx from 'clsx'
import Link from 'next/link'
import React from 'react'

import { formatPrice } from '@/_lib/utils'
import {
	IPreventiveExamination,
	IAnnualProgram,
	IMedanteCreditProgram,
	PROGRAM_CARD_TYPE_ENUM
} from '@/_types'
import { pushSelectItem, type GA4ListContext } from '@/_utils/analytics/datalayer'
import { processLink } from '@/_utils/link.utils'

/**
 * Splits program name into two lines if it contains "medante" and has at least 3 words
 * @param name - The program name to split
 * @returns Object with firstLine and secondLine (secondLine is null if no split needed)
 */
const splitProgramName = (name: string): { firstLine: string; secondLine: string | null } => {
	const nameLower = name.toLowerCase()
	const words = name.trim().split(/\s+/)

	// Check if name contains "medante" and has at least 3 words
	if (nameLower.includes('medante') && words.length >= 3) {
		// Find the index of the word containing "medante"
		const medanteIndex = words.findIndex(word => word.toLowerCase().includes('medante'))

		if (medanteIndex !== -1) {
			// First line: up to and including the word with "medante"
			const firstLine = words.slice(0, medanteIndex + 1).join(' ')
			// Second line: everything after
			const secondLine = words.slice(medanteIndex + 1).join(' ')

			return { firstLine, secondLine: secondLine || null }
		}
	}

	// No split needed
	return { firstLine: name, secondLine: null }
}

interface IProps {
	program: IPreventiveExamination | IAnnualProgram | IMedanteCreditProgram
	isFeatured?: boolean
	subtitle?: string
	description?: string | null
	headerRight?: React.ReactNode
	className?: string
	ga4List?: GA4ListContext
	ga4ProductType?: PROGRAM_CARD_TYPE_ENUM
}

const AContentCardHeaderContent = ({
	program,
	isFeatured = false,
	subtitle,
	description,
	headerRight
}: {
	program: IPreventiveExamination | IAnnualProgram | IMedanteCreditProgram
	isFeatured?: boolean
	subtitle?: string
	description?: string | null
	headerRight?: React.ReactNode
	className?: string
}) => {
	const { firstLine, secondLine } = splitProgramName(program.name)

	return (
		<>
			<div className='group w-full relative'>
				{subtitle && <p className='item_complex-health_subtitle'>{subtitle}</p>}
				<h3 className='item_complex-health_title'>
					{firstLine}
					{secondLine && (
						<>
							<br />
							{secondLine}
						</>
					)}
				</h3>
				{description && <p className='item_complex-health_description'>{description}</p>}
				<div className='relative'>
					<div className='flex items-center justify-between'>
						<div className='flex gap-1 text-lg'>
							<span className='price-currency'>
								{formatPrice(program.price).currency}
							</span>
							<span className='price-years'>
								<strong>{formatPrice(program.price).price}</strong>

								{'priceOld' in program && program.priceOld ? (
									<span className='price-old line-through ml-2 !inline'>
										{formatPrice(program.priceOld).price}
									</span>
								) : (
									''
								)}
							</span>
						</div>

						{program.url && program.url.trim().length > 0 && !headerRight && (
							<div className='item_complex-health_action-btn'>
								<span>
									<svg className='sico' aria-hidden='true'>
										<use href={`/assets/layout/icons.svg#s_ar2_r`} />
									</svg>
								</span>
								<span>
									<svg className='sico' aria-hidden='true'>
										<use href={`/assets/layout/icons.svg#s_ar2_r`} />
									</svg>
								</span>
							</div>
						)}
					</div>
				</div>

				{isFeatured && (
					<svg className='sico lg:!right-[-45px] !right-[-32px] !top-[-45px] !h-[70px] !w-[70px] '>
						<g>
							<use href='/assets/layout/icons.svg#s_fav_p1' className='!text-white' />
						</g>
						<g className='anim_rotate'>
							<use href='/assets/layout/icons.svg#s_fav_p2' className='!text-white' />
						</g>
					</svg>
				)}
			</div>
			{headerRight && <div className='item_complex-health_top_right'>{headerRight}</div>}
		</>
	)
}

const AContentCardHeader = ({
	program,
	isFeatured = false,
	subtitle,
	description,
	headerRight,
	className,
	ga4List,
	ga4ProductType
}: IProps) => {
	const handleSelectItem = () => {
		if (!ga4List || !ga4ProductType) return
		const priceOld =
			'priceOld' in program ? (program.priceOld as number | null | undefined) : undefined
		pushSelectItem(
			{
				id: program.id,
				name: program.name,
				type: ga4ProductType,
				price: program.price,
				priceOld
			},
			ga4List
		)
	}

	return (
		<div
			className={clsx(
				'item_complex-health_top',
				headerRight && 'item_complex-health_top_with-right-content',
				program.url && 'item_complex-health_top_has-link',
				className
			)}
		>
			{program.url && program.url.trim().length > 0 && !headerRight ? (
				<Link
					{...processLink(program.url)}
					title='Zobraziť program'
					prefetch={true}
					onClick={handleSelectItem}
				>
					<AContentCardHeaderContent
						program={program}
						isFeatured={isFeatured}
						subtitle={subtitle}
						description={description}
						headerRight={headerRight}
						className={className}
					/>
				</Link>
			) : (
				<div className='w-full flex justify-between items-center'>
					<AContentCardHeaderContent
						program={program}
						isFeatured={isFeatured}
						subtitle={subtitle}
						description={description}
						headerRight={headerRight}
						className={className}
					/>
				</div>
			)}
		</div>
	)
}

export default AContentCardHeader
